import store from './store';
import {getSharedDocumentHeader} from './data/apiUtils';
import {selectEffectiveOrg} from './data/session';

const baseUrl = process.env.REACT_APP_LIVEBLOCKS_API_URL;

const authEndpoint = async (room) => {
  const state = store.getState();
  const {session} = state;
  const effectiveOrg = selectEffectiveOrg(state);

  if (!session || !session.accessToken) {
    throw new Error('Authorization token is missing');
  }

  const headers = {
    Authorization: `Bearer ${session.accessToken}`,
    'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
    'Content-Type': 'application/json',
    ...getSharedDocumentHeader(),
  };

  const body = JSON.stringify({room});
  const response = await fetch(`${baseUrl}/api/liveblocksAuth`, {
    method: 'POST',
    headers,
    body,
  });

  return await response.json();
};

// Used only for Comments and Notifications. Return a list of user information
// retrieved from `userIds`. This info is used in comments, mentions etc.
// Return in same order as userIds argument, and include only the meta info
const resolveUsers = async ({userIds}, documentId) => {
  const state = store.getState();
  const {session} = state;
  const effectiveOrg = selectEffectiveOrg(state);

  if (!session || !session.accessToken) {
    throw new Error('Authorization token is missing');
  }

  const headers = {
    Authorization: `Bearer ${session.accessToken}`,
    'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
    'Content-Type': 'application/json',
    ...getSharedDocumentHeader(),
  };

  const searchParams = new URLSearchParams(
    userIds.map((userId) => ['userIds', userId])
  );
  if (documentId) {
    searchParams.set('documentId', documentId);
  }

  const response = await fetch(
    `${baseUrl}/api/liveblocksUsers?${searchParams}`,
    {
      method: 'GET',
      headers,
    }
  );

  const userData = await response.json();

  // Generate avatar URLs
  userData.forEach((user) => {
    user.avatar = generateAvatarUrl(user.name, user.color);
  });

  return userData;
};

// Used only for Comments. Return a list of userIds that match `text`.
// These userIds are used to create a mention list when typing in the
// composer.
let resolveMentionSuggestionsAbortController = new AbortController();
const resolveMentionSuggestions = async ({text}, documentId) => {
  const state = store.getState();
  const {session} = state;
  const effectiveOrg = selectEffectiveOrg(state);

  if (!session || !session.accessToken) {
    throw new Error('Authorization token is missing');
  }

  // Abort the previous request if it's still ongoing
  if (resolveMentionSuggestionsAbortController) {
    resolveMentionSuggestionsAbortController.abort();
  }

  // Create a new AbortController for the current request
  resolveMentionSuggestionsAbortController = new AbortController();

  const headers = {
    Authorization: `Bearer ${session.accessToken}`,
    'x-inscope-org': effectiveOrg?.orgId ?? session.currentOrg?.orgId,
    'Content-Type': 'application/json',
    ...getSharedDocumentHeader(),
  };

  const searchParams = new URLSearchParams();
  // URLSearchParams automatically encodes values,
  // so we don't need to call encodeURIComponent manually.
  if (typeof text === 'string') {
    searchParams.set('text', text);
  }
  if (documentId) {
    searchParams.set('documentId', documentId);
  }

  const response = await fetch(
    `${baseUrl}/api/liveblocksUsers/search?${searchParams.toString()}`,
    {
      method: 'GET',
      headers,
      signal: resolveMentionSuggestionsAbortController.signal,
    }
  );

  return await response.json();
};

const resolveRoomsInfo = async ({roomIds}) => {
  return []; // No additional logic for rooms info
};

const throttle = 100;

const generateAvatarUrl = (name, backgroundColor) => {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');
  const size = 80; // We'll use this for both width and height
  canvas.width = size;
  canvas.height = size;

  // Draw circular background
  context.beginPath();
  context.arc(size / 2, size / 2, size / 2, 0, 2 * Math.PI);
  context.fillStyle = backgroundColor;
  context.fill();

  // Draw text
  context.font = 'bold 38px Arial, sans-serif';
  context.fillStyle = 'white';
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(name.charAt(0).toUpperCase(), size / 2, size / 2 + 2); // +2 for slight vertical adjustment

  return canvas.toDataURL('image/png');
};

export {
  authEndpoint,
  resolveUsers,
  resolveMentionSuggestions,
  resolveRoomsInfo,
  throttle,
};
